/**
 * "Is this mail an RFQ?" — a FRONTEND heuristic, deliberately not an LLM.
 *
 * ---- Why this is not AI, and why it lives on the frontend ----
 *
 * `CLAUDE.md`'s FE/BE split reserves **"AI extraction + confidence"** for the backend, and
 * `docs/bosun-frappe-open-questions.md` Q8 records that Quoflow has no extraction method, no
 * extraction log and no confidence field today. So a model-based classifier is not ours to build:
 * an Anthropic API key can never reach browser code, and standing one up in the mock would
 * duplicate work the Frappe team owns.
 *
 * What IS ours is display logic derived from data we already hold — the same category as the
 * "Awaiting N days" badge. This module reads only the subject, the body text and the attachment
 * kinds that `get_email_communications` already returned, and scores them against an explicit,
 * readable signal table. Every number below is a weight a human chose and can argue with; there
 * is no model, no network call and no key.
 *
 * When the backend ships real extraction (Q8), this becomes the fallback for mail the server
 * hasn't scored yet — or is deleted outright. It is recorded as a frontend stand-in in
 * `docs/bosun-frontend-degradations.md`.
 *
 * ---- What `confidence` actually means ----
 *
 * It is a **saturating sum of the matched signal weights, clamped to 0–1. It is NOT a calibrated
 * probability** — nothing here has been fitted against labelled mail, so "0.8" does not mean
 * "right 80% of the time". It means "the signals that fired are worth 0.8 on the table below".
 * The UI must therefore always be able to show `signals` alongside the number, so the score is
 * auditable rather than an oracle: a user who disagrees can see exactly which rule fired.
 *
 * ---- Calibrated against the real mailbox ----
 *
 * The live Quoflow dev mailbox (Q22) holds five messages: one genuine RFQ,
 * `"Fwd: AMS-RFQ-7603 Request For Quotation"` with an `.xlsx` item list, and four Google account
 * notices ("Security alert" ×3, "2-Step Verification turned on"). That 1-in-5 ratio is why the
 * automated-notification signal carries real weight: without it, a keyword-only classifier calls
 * everything unclear and the badge is noise.
 */

import { htmlToText, isInboundFolder } from './mail'
import type { MailMessage } from './types'

export type RfqVerdict = 'rfq' | 'unclear' | 'not-rfq'

export interface RfqSignal {
  id: string
  /** Shown to the user in the badge tooltip — phrase it as evidence, not as jargon. */
  label: string
  weight: number
}

export interface RfqClassification {
  verdict: RfqVerdict
  /** 0–1, clamped. See the module header: a weighted signal sum, not a probability. */
  confidence: number
  /** Exactly the signals that fired, in table order. Never hide this from the user. */
  signals: RfqSignal[]
}

/**
 * A forwarded thread can run to tens of kilobytes of quoted history. Only the top of a mail
 * carries its intent, and this runs on every row of the list on every render, so the body is
 * scanned to a bounded prefix. Cost: an RFQ whose only give-away sits below 4 KB of quoted
 * history scores on its subject alone.
 */
const BODY_SCAN_LIMIT = 4000

/**
 * A Quoflow RFQ reference as it really appears — `AMS-RFQ-7603` in the live mailbox — plus the
 * bare `RFQ-1042` / `RFQ 7603` forms. The optional leading prefix is what makes this specific
 * enough to outrank everything else in the table.
 */
const RFQ_REFERENCE = /\b(?:[a-z]{2,5}[-\s])?rfq[-\s]?\d{3,}\b/i

/** The phrase spelled out, which is how a first-contact enquiry from a new customer reads. */
const RFQ_PHRASE = /request\s+for\s+quot(?:ation|e)s?\b/i

/** `RFQ` as a standalone word. Weaker — it also appears in signatures and reply chains. */
const RFQ_TOKEN = /\brfq\b/i

/**
 * An explicit ask for pricing, the thing that makes an enquiry an enquiry.
 *
 * Note what is NOT here: a bare `\bquotation\b`. It overlaps `RFQ_PHRASE` — "request for
 * quotation" contains it — so counting it would score one phrase twice and let it clear the RFQ
 * threshold alone. Keeping the two patterns disjoint is why this needs no precedence rule, unlike
 * `RFQ_TOKEN`. Nothing real is lost: "kindly send your quotation" still matches the verb branch.
 * (`best quotation` IS matched, by the second branch — "best" immediately before it cannot be
 * part of "request for quotation".)
 */
const PRICE_REQUEST =
  /\b(?:kindly|please|pls|could\s+you)\s+(?:quote|provide|send|submit|offer|advise|confirm|furnish|revert)\b|\bbest\s+(?:price|prices|offer|rates?|quotation)\b/i

/**
 * The customer naming the thing themselves. Strongly diagnostic and cheap: a supplier answering a
 * quote does not call their own mail an enquiry.
 */
const ENQUIRY_WORD = /\b(?:en|in)quir(?:y|ies)\b/i

/**
 * A supplier's REPLY — the mail that carries prices rather than asking for them.
 *
 * This exists because RFQ vocabulary alone cannot tell the two apart, and they need opposite
 * treatment: `/mail`'s action is "Start RFQ" on a customer's enquiry, while a supplier's reply
 * belongs to the Supplier Quotations screen (`isCustomerMail` in `./mail.ts` draws the same line).
 * Without this, a reply titled "RE: RFQ-2202 quotation" outscores the customer enquiry that
 * caused it — the reply quotes the reference number AND attaches a workbook, so it collects the
 * two heaviest positive signals while asking for nothing.
 */
const SUPPLIER_REPLY =
  /\bour\s+(?:quotation|quote|rates?|offer|best\b)|\bquotation\s+(?:attached|enclosed)\b|\battached\s+(?:is\s+|are\s+|herewith\s+)?our\b|\bwe\s+(?:are\s+pleased\s+to\s+|hereby\s+)?(?:quote|offer)\b|\bplease\s+find\s+(?:attached\s+)?our\b/i

/** Ship-chandler vocabulary — weak on its own, useful as corroboration. */
const MARINE_VOCABULARY = /\b(?:vessel|imo\s*no|impa|m\.?v\.?\s|port\s+of\s+delivery|eta\b|provisions?|bonded\s+stores)\b/i

/** Machine senders. `get_email_communications` gives us the raw address, so match on it. */
const AUTOMATED_SENDER = /\b(?:no-?reply|do-?not-?reply|mailer-daemon|postmaster|notifications?)@/i

/** Account/security housekeeping — four of the five messages in the live mailbox. */
const AUTOMATED_SUBJECT =
  /\b(?:security\s+alert|2-?step\s+verification|verify\s+your|password\s+(?:reset|changed)|sign-?in\s+attempt|unsubscribe|delivery\s+status\s+notification)\b/i

/** Above this, the badge asserts "RFQ". */
export const RFQ_THRESHOLD = 0.6

/**
 * At or below this, we say nothing — the mail is confidently not an enquiry.
 *
 * Deliberately below the `token` weight (0.25): a threshold at or above it would make the bare
 * "mentions RFQ" signal unable to lift anything out of `not-rfq` on its own, i.e. a rule that
 * fires and changes nothing.
 */
export const NOT_RFQ_THRESHOLD = 0.2

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

/**
 * Score one message. Pure: same input, same output, no clock and no network.
 *
 * Signal precedence worth knowing about: the automated-notification penalty is **suppressed**
 * when a hard RFQ reference or the spelled-out phrase is present. Otherwise a genuine RFQ blasted
 * out by a supplier portal from `noreply@` would be scored as a security notice. The penalty
 * exists to reject Google account mail, not to overrule direct evidence of an enquiry.
 */
export function classifyRfqMail(mail: MailMessage): RfqClassification {
  const subject = mail.subject
  const body = htmlToText(mail.body_html).slice(0, BODY_SCAN_LIMIT)
  const haystack = `${subject}\n${body}`

  const signals: RfqSignal[] = []
  const add = (id: string, label: string, weight: number) => signals.push({ id, label, weight })

  const hasReference = RFQ_REFERENCE.test(haystack)
  const hasPhrase = RFQ_PHRASE.test(haystack)

  if (hasReference) add('reference', 'Carries an RFQ reference number', 0.55)
  if (hasPhrase) add('phrase', 'Says “request for quotation”', 0.45)
  // Only when neither stronger form matched — `AMS-RFQ-7603` already counted the same word once.
  if (!hasReference && !hasPhrase && RFQ_TOKEN.test(haystack)) add('token', 'Mentions “RFQ”', 0.25)

  // No suppression needed — PRICE_REQUEST is disjoint from RFQ_PHRASE by construction; see its
  // comment. So "request for quotation … kindly quote your best price" correctly scores both,
  // because the second sentence is independent evidence rather than the same words again.
  if (PRICE_REQUEST.test(haystack)) add('price', 'Asks for prices', 0.2)
  if (ENQUIRY_WORD.test(haystack)) add('enquiry', 'Calls itself an enquiry', 0.25)
  if (mail.attachments.some((attachment) => attachment.kind === 'xlsx')) {
    add('workbook', 'Spreadsheet attached (likely an item list)', 0.2)
  }
  if (MARINE_VOCABULARY.test(haystack)) add('marine', 'Uses ship-supply vocabulary', 0.1)

  // Pushes a supplier's answer back below the "RFQ" assertion without hiding it entirely — a
  // reply still carries a real reference number, so "unclear" is the honest landing place.
  if (SUPPLIER_REPLY.test(haystack)) add('reply', 'Reads like a supplier’s reply, not a request', -0.5)

  // See the precedence note above — direct RFQ evidence outranks the machine-sender heuristic.
  if (!hasReference && !hasPhrase && (AUTOMATED_SENDER.test(mail.from) || AUTOMATED_SUBJECT.test(subject))) {
    add('automated', 'Looks like an automated notification', -0.9)
  }

  const confidence = clamp01(signals.reduce((total, signal) => total + signal.weight, 0))
  const verdict: RfqVerdict =
    confidence >= RFQ_THRESHOLD ? 'rfq' : confidence <= NOT_RFQ_THRESHOLD ? 'not-rfq' : 'unclear'

  return { verdict, confidence, signals }
}

/** `0.82` → `"82%"`. The badge's own label; kept here so the rounding rule has one home. */
export const confidencePercent = (confidence: number): string => `${Math.round(confidence * 100)}%`

/**
 * The tooltip body: every signal that fired, with its weight, then the caveat. Users act on this
 * badge, so the evidence and the "not a probability" warning travel with it — see the module
 * header on why the number must never stand alone.
 */
export function explainClassification(result: RfqClassification): string {
  const lines = result.signals.map(
    (signal) => `${signal.weight > 0 ? '+' : '−'}${Math.abs(Math.round(signal.weight * 100))}  ${signal.label}`,
  )
  if (lines.length === 0) lines.push('No RFQ signals found.')
  return `${lines.join('\n')}\n\nKeyword score, not a trained model — check the mail itself.`
}

/* ============================================================================================
 * Which SIDE of the conversation a mail is — a fact, not a guess
 * ============================================================================================ */

/**
 * Whether an inbound mail is a supplier answering one of OUR RFQs, rather than a customer sending
 * us a new enquiry.
 *
 * ---- Why this is not the classifier above, and not a model ----
 *
 * `classifyRfqMail` scores "does this look like a request for quotation" from language. This asks
 * a different question with a different kind of answer: every RFQ and reminder Bosun sends carries
 * `[<enquiry-id>]` in its subject (`buildRfqDrafts`, `buildReminderDraft`), and a reply keeps it.
 * So an INBOUND mail carrying that tag is, by construction, a reply to something we sent. There is
 * nothing to infer and no call to spend — the tag is ours and we put it there.
 *
 * It is also the same mechanism Frappe uses to link the mail to its RFQ (`reference_name`,
 * verified live 2026-08-07), so agreeing with it keeps one story rather than two.
 *
 * ---- Why the direction check is load-bearing ----
 *
 * Our own outgoing RFQs carry the tag too. Applied to a Sent-folder mail this would call our own
 * message a supplier reply, so the folder is checked via `isInboundFolder` — the same predicate
 * `ReaderPane` uses — rather than left implicit in whichever list happens to render it.
 *
 * ---- The false negative it accepts ----
 *
 * A supplier who replies from a fresh compose, dropping the subject, is missed and reads as an
 * ordinary mail. That is the safe direction: it stays in the inbox to be handled by hand, rather
 * than a quotation being attributed to an enquiry it may not belong to.
 */
/**
 * TWO tag formats, because two senders compose these mails.
 *
 * - `[ENQ-2026-00019]` — what WE put in the subject (`buildRfqDrafts`, `buildReminderDraft`).
 * - `[REQ-RFQ-2026-00010]` — what the SERVER puts there when it composes the mail itself. It is
 *   the RFQ's own `req_tag` field, and it names the **RFQ**, not the enquiry.
 *
 * Measured 2026-08-07: sending through `send_rfq_email_with_excel` WITHOUT a subject produced
 * `"Request for Quotation — RFQ-2026-00010 [REQ-RFQ-2026-00010]"`. The original pattern here
 * accepted `REQ-<digits>` only, so every reply to a server-composed RFQ was missed.
 *
 * The server's form is the more useful of the two — it identifies which RFQ was answered, which
 * the enquiry tag cannot (one enquiry, several suppliers). `repliedRfqId` exposes that.
 */
const ENQUIRY_TAG = /\[(REQ-RFQ-\d{4}-\d{4,}|ENQ-\d{4}-\d{4,}|OPP-\d{4}-\d{4,}|REQ-\d+)\]/i

/**
 * OUR OWN naming series, unbracketed, anywhere in a subject or body.
 *
 * `RFQ-2026-00013` / `ENQ-2026-00019` are names WE assign (`RFQ-.YYYY.-`, `ENQ-.YYYY.-`). A
 * customer's own inbound enquiry quotes THEIR reference — `MAT-MR-2026-03157`, `RMB-0059-26`, both
 * real in this mailbox — and neither matches. So a bare occurrence of our series in an inbound mail
 * is strong evidence it answers something we sent, not a fresh request.
 *
 * Deliberately NOT `\d+`: a loose pattern would match the customer references above.
 */
const OWN_DOC_NUMBER = /\b(?:RFQ|ENQ|OPP)-\d{4}-\d{4,}\b/i

/**
 * Does this inbound mail answer something we sent?
 *
 * Three tiers of evidence, strongest first:
 *
 *  1. The server LINKED it (`reference_doctype: "RFQ"`). Certainty, not inference — but measured
 *     2026-08-08, `mail.get_emails` does not return `reference_doctype`/`reference_name` at all,
 *     so nothing on the mail wire can read it yet. Filed as an ask; `linked` is threaded through
 *     here so the day those fields arrive this function needs no restructuring.
 *  2. A bracketed `[REQ-RFQ-…]` / `[ENQ-…]` tag — what the server composes on our RFQ subjects.
 *  3. A BARE occurrence of our own document number in the subject or body.
 *
 * Tier 3 exists because of a real mail: `ar9f1eknij`, subject `RFQ-2026-00013"`, body "Below are
 * the quotation for the RFQ-2026-00013". The supplier composed fresh rather than replying, so the
 * server left it unlinked AND it carries no bracketed tag — and this screen badged a supplier's
 * quotation as an incoming RFQ at 55%, offering to create an enquiry from it.
 */
/**
 * OUR OWN outgoing RFQ or reminder, by its exact subject — `Request for Quotation — RFQ-… [REQ-…]`
 * with no Re:/Fwd: in front. It can turn up in the INBOX (2026-09-19): the purchaser's own
 * mailbox is configured as an inbound Email Account on the site, so the Cc copy of every RFQ we
 * send is fetched back as Received. That is our mail, not a supplier's reply, whatever folder
 * holds it.
 */
export function isOwnOutgoingRfq(subject: string | null | undefined): boolean {
  const s = (subject ?? '').trim()
  if (/^(re|fw|fwd)\s*:/i.test(s)) return false
  return /^(reminder:\s*)?request for quotation\b/i.test(s) && ENQUIRY_TAG.test(s)
}

export function isSupplierReply(
  mail: Pick<MailMessage, 'subject' | 'folder'> & Partial<Pick<MailMessage, 'preview' | 'body_html'>>,
  /** Tier 1, when a caller has it. See above for why the mail wire cannot supply this today. */
  linked?: boolean,
): boolean {
  if (!isInboundFolder(mail.folder)) return false
  if (isOwnOutgoingRfq(mail.subject)) return false
  if (linked === true) return true

  const subject = mail.subject ?? ''
  if (ENQUIRY_TAG.test(subject)) return true
  if (OWN_DOC_NUMBER.test(subject)) return true

  // The body only when the subject says nothing — a quoted thread can mention a number from an
  // unrelated conversation, so it is the weakest of the three and never the sole loud signal.
  const body = mail.preview ?? (mail.body_html ? htmlToText(mail.body_html) : '')
  return OWN_DOC_NUMBER.test(body.slice(0, BODY_SCAN_LIMIT))
}

/** The enquiry a reply belongs to, straight out of its subject tag — `null` when there is none. */
export function repliedEnquiryId(subject: string | null | undefined): string | null {
  const m = ENQUIRY_TAG.exec(subject ?? '')
  return m ? m[1].toUpperCase() : null
}

/**
 * The RFQ a reply answers, when the subject carries the SERVER's `[REQ-RFQ-…]` tag.
 *
 * Returns `null` for our own `[ENQ-…]` tag, which names only the enquiry — a deliberate refusal
 * rather than a guess: one enquiry has several RFQs out to several suppliers, and attributing a
 * quotation to the wrong one is worse than leaving it for the purchaser to place.
 */
export function repliedRfqId(subject: string | null | undefined): string | null {
  const m = /\[REQ-(RFQ-\d{4}-\d{4,})\]/i.exec(subject ?? '')
  return m ? m[1].toUpperCase() : null
}
